import { useAppSelector } from "app/store";
import { useGaiaObjectUiContent } from "features/ui-content";

/**
 * Determines the UI content type based on team settings.
 *
 * IMPORTANT RESTRICTION: This method has a fundamental limitation - it arbitrarily selects
 * one UI content type based on code sequence (litigation > notice > audit), which may not
 * reflect the actual business context needed for the page.
 *
 * ONLY USE when:
 * - The detailed UI content properties are IDENTICAL across all three categories
 *   (audit, notice, litigation)
 * - Examples: taxArea page, tasks page
 */
export const getUiContentType = (teamSettings: any): string => {
  if (
    teamSettings?.enabledFeatures?.includes("LITIGATION") ||
    teamSettings?.enabledFeatures?.includes("litigation")
  ) {
    return "litigation";
  }
  if (
    teamSettings?.teamCustomAttributesSchema?.notice ||
    teamSettings?.enabledFeatures?.includes("NOTICE") ||
    teamSettings?.enabledFeatures?.includes("notice")
  ) {
    return "notice";
  }
  return "audit"; // Default to audit/createaudit
};

/**
 * Hook to get the appropriate UI content based on team settings.
 *
 * IMPORTANT RESTRICTION: This hook inherits the same limitations as getUiContentType().
 * It arbitrarily selects UI content type without considering the actual business context.
 */
export const useTeamBasedUiContent = () => {
  const teamSettings = useAppSelector(
    (state) => state.teamSettings.selectedTeamMainMetaData,
  );
  const uiContentType = getUiContentType(teamSettings);
  return useGaiaObjectUiContent(uiContentType);
};
